import "reactflow/dist/style.css";
import { IconButton } from "@altimateai/lego";
import { Lineage, LineageRef, StaticLineageDetails } from "@altimateai/ui-components/lineage";
import { useEffect, useMemo, useRef, useState } from "react";
import { destructTable } from "./utils";
import { FullscreenIcon } from "@/assets/icons";

interface StaticLineageProps {
  selectedColumn: { table: string; name: string };
  collectColumns: Record<string, string[]>;
  columnEdges: [string, string, string][];
  tableEdges: [string, string][];
  tables: string[];
  height?: string;
}

const StaticLineage = ({
  selectedColumn,
  collectColumns: _collectColumns = {},
  columnEdges,
  tableEdges,
  tables,
  height = "40vh",
}: StaticLineageProps) => {
  const details = useMemo(() => {
    const result: Record<string, { type: string; name: string }> = {};
    for (const curr of tables) {
      const [, schema, label] = destructTable(curr);
      result[curr] = { type: schema.split(".")[0], name: label };
    }
    return result;
  }, [tables]);
  const collectColumns = useMemo(() => {
    const result: Record<string, { column: string }[]> = {};
    for (const curr in _collectColumns) {
      result[curr] = _collectColumns[curr].map(column => ({ column }));
    }
    return result;
  }, [_collectColumns]);
  return (
    <div style={{ height }}>
      <Lineage
        theme={"light"}
        staticLineage={{
          details,
          tableEdges,
          selectedColumn,
          collectColumns,
          columnEdges,
        }}
        lineageType={"static"}
        client="saas"
      />
    </div>
  );
};

interface SqlLineageProps {
  details?: StaticLineageDetails;
  tableEdges?: [string, string][];
  nodePositions?: Record<string, [number, number]>;
  height?: string;
  externalSidePanel?: boolean;
}

const SqlLineage = ({
  details,
  tableEdges,
  nodePositions,
  height,
  externalSidePanel = false,
}: SqlLineageProps) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const lineageRef = useRef<LineageRef | null>(null);
  const [isInFullScreen, setIsInFullScreen] = useState(false);

  useEffect(() => {
    lineageRef.current?.rerender();
  }, [isInFullScreen]);

  useEffect(() => {
    const onFullScreenChange = () => {
      setIsInFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", onFullScreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", onFullScreenChange);
    };
  }, []);

  const handleFullScreenClick = () => {
    if (!elementRef.current) return;

    try {
      elementRef.current.requestFullscreen();
    } catch (e) {
      console.error("Failed to enter full screen", e);
    }
  };

  return (
    <div
      ref={elementRef}
      className={"al-h-[80vh] al-relative"}
      style={{ height: isInFullScreen ? "100vh" : height }}
      aria-label="Sql lineage"
    >
      {!isInFullScreen ? (
        <IconButton
          variant="ghost"
          title="Enter fullscreen"
          className={`al-p-0 al-absolute al-right-[10px] al-z-1`}
          onClick={handleFullScreenClick}
        >
          <FullscreenIcon className="!al-w-[24px] !al-h-[24px]" />
        </IconButton>
      ) : null}
      {details && tableEdges && (
        <Lineage
          theme={"light"}
          sqlLineage={{ details, tableEdges, nodePositions: nodePositions ?? undefined }}
          lineageType={"sql"}
          externalSidePanel={externalSidePanel}
          ref={lineageRef}
          client="saas"
        />
      )}
    </div>
  );
};

export { SqlLineage, StaticLineage };
